// "Merge together": a person picks ready proposals under Ready to merge, and
// the server lands them as one landing, a batch (`merge_together`). Here are
// its rules as the page meets them, and its words: which landings are
// batches, the step a batch is at, what a card in it says, why one stopped,
// and why a Merge together was refused. A batch that stopped has its PM
// agent (Poulpe-406), whose retries are batches too, and what it says it is
// doing.
import { MERGE_MAX, type BlockReason, type Landing, type LandingOutcome, type Proposal, type Session } from '../api/types'
import { agentName } from '../code/format'
import type { MergeOutcome } from '../store/store'

/**
 * A landing the person asked for: several proposals, or one waiting for
 * another landing, or a PM agent's retry, of one proposal too. A queue's own
 * landing is one proposal's.
 */
export function isBatch(landing: Landing): boolean {
  return landing.proposals.length > 1 || landing.state === 'waiting' || pmOf(landing) !== null
}

/** Its PM agent's session, if it has one; none on a landing kept from before the field. */
export function pmOf(landing: Landing): string | null {
  return landing.pm_session ?? null
}

/** A stopped batch's retries that run or wait: its PM agent's other landings. */
export function retriesOf(stopped: Landing, landings: ReadonlyMap<string, Landing>): Landing[] {
  const pm = pmOf(stopped)
  if (!pm) return []
  return [...landings.values()].filter((l) => l.id !== stopped.id && l.state !== 'finished' && pmOf(l) === pm)
}

/** The PM agent by its name: `Claude, the PM agent`, from the project's setting. */
export function pmName(agent: string): string {
  return `${agentName(agent)}, the PM agent`
}

/** What a PM agent is doing, from its session, as a card's live line reads one; null while the page has not its session. */
export function pmDoing(session: Session | undefined): 'starting' | 'working' | 'waiting for you' | 'done' | null {
  switch (session?.state) {
    case 'starting':
      return 'starting'
    case 'running':
      return 'working'
    case 'idle':
      return 'waiting for you'
    case 'ended':
      return 'done'
    default:
      return null
  }
}

/** A project's batches that run or wait, the running one first. */
export function batchLandings(project: string | null, landings: ReadonlyMap<string, Landing>): Landing[] {
  return [...landings.values()]
    .filter((l) => l.project_id === project && l.state !== 'finished' && isBatch(l))
    .sort((a, b) => Number(a.state === 'waiting') - Number(b.state === 'waiting'))
}

/** The batch that runs or waits with this proposal in it, if any. */
export function mergeOf(proposal: string, landings: ReadonlyMap<string, Landing>): Landing | null {
  return [...landings.values()].find((l) => l.state !== 'finished' && isBatch(l) && l.proposals.includes(proposal)) ?? null
}

/** Whether these proposals, in this order, may be sent: 2 to MERGE_MAX, or one a batch holds. */
export function canMerge(picked: readonly Proposal[]): boolean {
  return picked.length === 1 ? picked[0]!.held_by !== null : picked.length >= 2 && picked.length <= MERGE_MAX
}

/** Picking a proposal adds it last; picking it again takes it out, and those after it move up. */
export function togglePick(picked: readonly string[], id: string): string[] {
  return picked.includes(id) ? picked.filter((p) => p !== id) : [...picked, id]
}

export type MergeStep = 'combine' | 'check' | 'approve' | 'land'

export const MERGE_STEPS: readonly MergeStep[] = ['combine', 'check', 'approve', 'land']

export const STEP_WORDS: Record<MergeStep, string> = { combine: 'Combine', check: 'Check', approve: 'Approve', land: 'Land' }

/** What a card in the merge says it is doing, by step. */
const CARD_WORDS: Record<MergeStep, string> = { combine: 'combining', check: 'checking', approve: 'waiting for your approval', land: 'landing' }

/** The step a batch is at, from what it waits for; none while it waits for another landing. */
export function stepOf(landing: Landing): MergeStep | null {
  if (landing.state === 'waiting' || (landing.state === 'deferred' && landing.waiting_for?.for !== 'approval')) return null
  switch (landing.waiting_for?.for) {
    case 'full_check':
      return 'check'
    case 'approval':
      return 'approve'
    case 'forge':
      return 'land'
    default:
      // The candidate, or nothing yet: it has just started.
      return 'combine'
  }
}

/** What a card of a batch says: `In the merge · checking`. */
export function inMergeText(landing: Landing): string {
  const step = stepOf(landing)
  return `In the merge · ${step ? CARD_WORDS[step] : 'waiting'}`
}

/** What a waiting batch waits for: the running landing, by its first issue's key. */
export function waitingText(key: string | null): string {
  return key ? `Waiting for ${key}'s landing` : 'Waiting for another landing'
}

/** Keys in words: `A`, `A and B`, `A, B and C`. */
export function keysText(keys: readonly string[]): string {
  return keys.length <= 1 ? (keys[0] ?? '') : `${keys.slice(0, -1).join(', ')} and ${keys.at(-1)}`
}

/** Why a batch was blocked, in a few words, to follow "Held after". */
function blockedText(reasons: readonly BlockReason[]): string {
  if (reasons.some((r) => r.reason === 'check_failed')) return 'the check failed'
  if (reasons.some((r) => r.reason === 'no_full_check')) return 'the check did not run'
  if (reasons.some((r) => r.reason === 'no_person_review')) return 'the project now asks for your review: ask for changes to have it proposed again'
  if (reasons.some((r) => r.reason === 'no_cross_vendor_review' || r.reason === 'review_rejected' || r.reason === 'person_rejected')) return 'a review did not allow it'
  return 'the check was not of this merge'
}

const capital = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

/**
 * Why a batch stopped, in one line, or null when it did not fail: it landed,
 * or its policy changed and it starts again. `keyOf` names a proposal.
 */
export function stoppedText(outcome: LandingOutcome | null, keyOf: (proposal: string) => string): string | null {
  switch (outcome?.outcome) {
    case 'candidate_not_built': {
      const conflict = outcome.conflict
      if (!conflict) return 'The merge could not be built'
      return `${keyOf(conflict.proposal)} conflicts with ${conflict.after ? keyOf(conflict.after) : 'main'} in ${conflict.paths.join(', ')}`
    }
    case 'blocked':
      return capital(blockedText(outcome.reasons))
    case 'check_not_run':
      return 'The check could not run'
    case 'cancelled':
      return outcome.message
    case 'candidate_refused':
      return `The merge candidate was refused: ${outcome.message}`
    case 'gave_up':
      return `It gave up: ${outcome.reason}`
    default:
      return null
  }
}

/** What a held card says: `Held after Poulpe-351's conflict with Poulpe-350`. Its batch may be unknown to the page. */
export function heldText(landing: Landing | undefined, keyOf: (proposal: string) => string): string {
  const outcome = landing?.outcome
  switch (outcome?.outcome) {
    case 'candidate_not_built':
      return outcome.conflict
        ? `Held after ${keyOf(outcome.conflict.proposal)}'s conflict with ${outcome.conflict.after ? keyOf(outcome.conflict.after) : 'main'}`
        : 'Held after the merge could not be built'
    case 'blocked':
      return `Held after ${blockedText(outcome.reasons)}`
    case 'check_not_run':
      return 'Held after the check could not run'
    case 'candidate_refused':
      return 'Held after the merge candidate was refused'
    case 'gave_up':
      return 'Held after the merge gave up'
    default:
      return 'Held after its merge stopped'
  }
}

/** What to tell the person when a Merge together was refused. */
export function refusalText(outcome: Extract<MergeOutcome, { ok: false }>): string {
  if (outcome.status === 403 && outcome.reason === 'merge_not_by_person') return 'Only a person merges proposals together.'
  if (outcome.status === 400) return `Pick 2 to ${MERGE_MAX} proposals, each once, or one that is held.`
  if (outcome.status === 409 && outcome.reason === 'not_ready') return 'One of them is no longer ready to merge.'
  if (outcome.status === 409 && outcome.reason === 'other_project') return 'They are not all of this project.'
  if (outcome.status === 404) return 'One of them is no longer on the board.'
  return `Could not merge: ${outcome.message}`
}
